
import {defineComponent, PropType} from "vue";
import {ImageRoundingRestDto} from "@/api/pharma-cms-content-block/models";
import {ImageRoundingRestDtoModel} from "@/models/api/pharma-cms-content-block/ImageRoundingRestDtoModel";
import {ComponentMixin} from "@/components/base/ComponentMixin";
import GenericComponentWrapper from "@/components/base/GenericComponentWrapper.vue";
import CssDimensionEditor from "@/components/layouts/content-pages/wysiwyg/misc/CssDimensionEditor.vue";
import MetadataGroupCard from "@/components/layouts/content-pages/wysiwyg/misc/MetadataGroupCard.vue";
import {CssUnitEnum} from "@/api/enums/css-unit-enum";

export default defineComponent({
  name: "ImageRoundingDetail",
  mixins: [ComponentMixin],
  components: {GenericComponentWrapper, CssDimensionEditor, MetadataGroupCard},
  emits: ['update:modelValue'],
  props: {
    // Null means the group is switched off: no rounding is applied.
    modelValue: {
      type: Object as PropType<ImageRoundingRestDto | null>,
      required: false,
      default: null,
    },
    name: {
      type: String,
      required: true,
    },
    errors: {
      type: Object,
      required: true,
    },
  },
  mounted() {
    // Reset baseline: the state as it is stored in the database. The parent
    // remounts this component after a (re)load or a successful save.
    this.persistedDto = this.modelValue ? ImageRoundingRestDtoModel.cloneFrom(this.modelValue) : null;

    this.componentUI.setReady();
  },
  data() {
    return {
      enabled: false as boolean,
      localDto: ImageRoundingRestDtoModel.createWithDefaults() as ImageRoundingRestDto,
      // Values entered before the group was switched off, so they can be restored.
      lastActiveDto: null as ImageRoundingRestDto | null,
      persistedDto: null as ImageRoundingRestDto | null,
    };
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newValue: ImageRoundingRestDto | null) {
        this.enabled = !!newValue;
        if (newValue) {
          this.localDto = ImageRoundingRestDtoModel.cloneFrom(newValue);
        }
      },
    },
  },
  computed: {
    CssUnitEnum() {
      return CssUnitEnum;
    },
  },
  methods: {
    onEnabledChanged(newValue: boolean): void {
      // The switch re-emits its current state on render; acting on that would
      // bounce an update back up and re-derive `enabled` again, without end.
      if (newValue === this.enabled) {
        return;
      }
      this.enabled = newValue;

      if (newValue) {
        this.localDto = this.lastActiveDto
          ? ImageRoundingRestDtoModel.cloneFrom(this.lastActiveDto)
          : ImageRoundingRestDtoModel.createWithDefaults();

        this.$emit('update:modelValue', this.localDto);
      } else {
        this.lastActiveDto = ImageRoundingRestDtoModel.cloneFrom(this.localDto);

        this.$emit('update:modelValue', null);
      }
    },
    resetToPersisted(): void {
      this.localDto = this.persistedDto
        ? ImageRoundingRestDtoModel.cloneFrom(this.persistedDto)
        : ImageRoundingRestDtoModel.createWithDefaults();

      this.$emit('update:modelValue', this.localDto);
    },
    // A corner of 0 px renders exactly like an absent corner, so it is stored as
    // null: "no rounding" stays distinguishable from "explicitly rounded".
    normalizeNeutralCorners(): void {
      if (this.localDto.top_left?.unit_dimension.value === 0) {
        this.localDto.top_left = null;
      }
      if (this.localDto.top_right?.unit_dimension.value === 0) {
        this.localDto.top_right = null;
      }
      if (this.localDto.bottom_right?.unit_dimension.value === 0) {
        this.localDto.bottom_right = null;
      }
      if (this.localDto.bottom_left?.unit_dimension.value === 0) {
        this.localDto.bottom_left = null;
      }
    },
    emitModelChanged(): void {
      this.normalizeNeutralCorners();

      this.$emit('update:modelValue', this.localDto);
    },
  },
});
